'use client';

import React, { useEffect, useRef, useState } from 'react';

interface MuxPlayerProps {
  playbackId: string;
  title?: string;
  poster?: string;
  className?: string;
  autoPlay?: boolean;
  startTime?: number;  // Start time in seconds
  muted?: boolean;
  loop?: boolean;
  thumbnailTime?: number; // Thumbnail time in seconds
  preload?: 'auto' | 'metadata' | 'none';
  controls?: boolean;
  primaryColor?: string; // Custom color for player controls
  secondaryColor?: string; // Custom color for player controls
  maxResolution?: string; // e.g., '720p'
  captionUrl?: string; // URL to VTT caption file
  captionLanguage?: string; // Language code for captions (e.g., 'en', 'es', 'fr')
  defaultShowCaptions?: boolean; // Whether to show captions by default
  streamType?: 'on-demand' | 'live'; // Stream type
  tokens?: {
    playback?: string; // Playback token for protected videos
  };
}

const MuxPlayer = React.forwardRef<any, MuxPlayerProps>(({
  playbackId,
  title,
  poster,
  className = '',
  autoPlay = false,
  startTime,
  muted = false,
  loop = false,
  thumbnailTime = 0,
  preload = 'metadata',
  controls = true,
  primaryColor,
  secondaryColor,
  maxResolution,
  captionUrl,
  captionLanguage = 'en',
  defaultShowCaptions = true,
  streamType = 'on-demand',
  tokens,
}, ref) => {
  // Validate playbackId - must be a non-empty string to prevent errors
  const validPlaybackId = typeof playbackId === 'string' && playbackId.trim() !== '' ? playbackId : null;
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const retryCountRef = useRef(0);

  useEffect(() => {
    // Check if the Mux Player script is already loaded
    // Using latest stable MUX Player version with better error handling
    if (!document.querySelector('script[src*="mux-player"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@mux/mux-player@2.4.1/dist/mux-player.js';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Function to check if video is available
  const checkVideoAvailability = async () => {
    if (!validPlaybackId) return false;
    
    try {
      // Try to fetch the video thumbnail as a quick check for availability
      const response = await fetch(`https://image.mux.com/${validPlaybackId}/thumbnail.jpg?time=0`, {
        method: 'HEAD',
      });
      
      if (response.ok) {
        console.log('[MuxPlayer] Video is ready and available for playback');
        setVideoReady(true);
        setCheckingAvailability(false);
        retryCountRef.current = 0;
        return true;
      }
    } catch (error) {
      console.log('[MuxPlayer] Video not ready yet, will retry...');
    }
    
    return false;
  };

  // Reset video ready state when playbackId changes
  useEffect(() => {
    setVideoReady(false);
    retryCountRef.current = 0;
  }, [validPlaybackId]);

  // Start checking video availability when playbackId changes
  useEffect(() => {
    if (validPlaybackId && !videoReady) {
      setCheckingAvailability(true);
      
      const checkAndRetry = async () => {
        const isReady = await checkVideoAvailability();
        
        if (!isReady && retryCountRef.current < 30) { // Max 30 retries (about 2 minutes)
          retryCountRef.current++;
          // Start with 2 second intervals, then slow down to 5 seconds after 10 retries
          const delay = retryCountRef.current < 10 ? 2000 : 5000;
          retryTimeoutRef.current = setTimeout(checkAndRetry, delay);
        } else if (!isReady) {
          console.log('[MuxPlayer] Max retries reached, video may not be available');
          setCheckingAvailability(false);
          // Still try to load the player - it might work
          setVideoReady(true);
        }
      };
      
      checkAndRetry();
    }
    
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [validPlaybackId, videoReady]);

  useEffect(() => {
    let keepAliveInterval: any = null;
    
    if (scriptLoaded && playerRef.current && validPlaybackId && videoReady) {
      // Clear existing player if any
      playerRef.current.innerHTML = '';

      // Create the mux-player element
      const muxPlayerEl = document.createElement('mux-player');
      
      // Required attributes - use validated playbackId
      muxPlayerEl.setAttribute('playback-id', validPlaybackId);
      
      // Set default subtitle language to ensure correct track selection
      muxPlayerEl.setAttribute('default-subtitles-lang', captionLanguage);
      
      // Add MUX Data environment key for analytics tracking
      const muxEnvKey = process.env.NEXT_PUBLIC_MUX_ENV_KEY;
      if (muxEnvKey) {
        muxPlayerEl.setAttribute('env-key', muxEnvKey);
      }
      
      // Optional attributes
      if (title) {
        muxPlayerEl.setAttribute('title', title);
      }
      
      // Poster setup
      if (poster) {
        muxPlayerEl.setAttribute('poster', poster);
      } else {
        // Use the default Mux thumbnail with specific time if provided
        const thumbnailUrl = `https://image.mux.com/${validPlaybackId}/thumbnail.jpg${
          thumbnailTime ? `?time=${thumbnailTime}` : ''
        }`;
        muxPlayerEl.setAttribute('poster', thumbnailUrl);
      }
      
      // Playback settings
      if (autoPlay) {
        muxPlayerEl.setAttribute('autoplay', '');
      }
      
      if (muted) {
        muxPlayerEl.setAttribute('muted', '');
      }
      
      if (loop) {
        muxPlayerEl.setAttribute('loop', '');
      }
      
      if (startTime !== undefined) {
        muxPlayerEl.setAttribute('start-time', startTime.toString());
      }
      
      if (preload) {
        muxPlayerEl.setAttribute('preload', preload);
      }
      
      // Player UI settings
      muxPlayerEl.setAttribute('stream-type', streamType);
      
      if (!controls) {
        muxPlayerEl.setAttribute('default-hidden-captions', '');
        muxPlayerEl.removeAttribute('controls');
      } else {
        muxPlayerEl.setAttribute('controls', '');
        // MUX automatically shows caption controls when captions are available
        // Set default caption visibility
        if (defaultShowCaptions) {
          muxPlayerEl.setAttribute('default-show-captions', '');
        }
      }
      
      // Custom colors
      if (primaryColor) {
        muxPlayerEl.setAttribute('primary-color', primaryColor);
      }
      
      if (secondaryColor) {
        muxPlayerEl.setAttribute('secondary-color', secondaryColor);
      }
      
      // Resolution limits
      if (maxResolution) {
        muxPlayerEl.setAttribute('max-resolution', maxResolution);
      }
      
      // Playback tokens for protected videos
      if (tokens?.playback) {
        muxPlayerEl.setAttribute('playback-token', tokens.playback);
      }
      
      // Styling
      muxPlayerEl.style.height = '100%';
      muxPlayerEl.style.width = '100%';
      muxPlayerEl.style.borderRadius = '0.5rem';
      muxPlayerEl.style.display = 'block';
      
      // Add to the DOM
      playerRef.current.appendChild(muxPlayerEl);
      
      // Add caption track if external URL is provided (fallback for non-MUX captions)
      // Note: If captions are uploaded to MUX, they're automatically included in the HLS stream
      if (captionUrl) {
        console.log('[MuxPlayer] Adding external caption track:', captionUrl);
        const trackEl = document.createElement('track');
        trackEl.setAttribute('kind', 'captions');
        trackEl.setAttribute('src', captionUrl);
        trackEl.setAttribute('srclang', captionLanguage);
        trackEl.setAttribute('label', `${captionLanguage.toUpperCase()} (External)`);
        if (defaultShowCaptions) {
          trackEl.setAttribute('default', '');
        }
        muxPlayerEl.appendChild(trackEl);
      }
      
      // Add event listeners
      muxPlayerEl.addEventListener('playing', () => {
        console.log('Video started playing');
      });
      
      muxPlayerEl.addEventListener('ended', () => {
        console.log('Video playback completed');
      });
      
      // Add error handling and recovery
      let errorRetryCount = 0;
      const maxErrorRetries = 3;
      
      muxPlayerEl.addEventListener('error', (event: any) => {
        console.error('[MuxPlayer] Media error occurred:', event);
        const error = event?.detail || event?.error || event;
        
        // Check if it's a buffer error or media error to handle recovery
        const isBufferError = error?.message?.includes('bufferAppendError') || 
                              error?.code === 'bufferAppendError' ||
                              error?.type === 'bufferAppendError';
        
        const isMediaError = error?.message?.includes('MediaError') ||
                            error?.code === 'MediaError' ||
                            error?.type === 'MediaError' ||
                            error?.message?.includes('media error');
        
        if ((isBufferError || isMediaError) && errorRetryCount < maxErrorRetries) {
          errorRetryCount++;
          console.log(`[MuxPlayer] Error detected (attempt ${errorRetryCount}/${maxErrorRetries}), attempting recovery...`);
          
          // Store current time
          const currentTime = muxPlayerEl.currentTime || 0;
          
          // Remove the existing player
          muxPlayerEl.remove();
          
          // Wait a bit then recreate the player
          setTimeout(() => {
            // Create a new mux-player element
            const newMuxPlayerEl = document.createElement('mux-player');
            newMuxPlayerEl.setAttribute('playback-id', validPlaybackId);
            newMuxPlayerEl.setAttribute('default-subtitles-lang', captionLanguage);
            
            // Add MUX Data environment key for analytics tracking
            const muxEnvKey = process.env.NEXT_PUBLIC_MUX_ENV_KEY;
            if (muxEnvKey) {
              newMuxPlayerEl.setAttribute('env-key', muxEnvKey);
            }
            
            // Copy all the attributes from the old player
            if (title) newMuxPlayerEl.setAttribute('title', title);
            if (poster) {
              newMuxPlayerEl.setAttribute('poster', poster);
            } else {
              const thumbnailUrl = `https://image.mux.com/${validPlaybackId}/thumbnail.jpg${
                thumbnailTime ? `?time=${thumbnailTime}` : ''
              }`;
              newMuxPlayerEl.setAttribute('poster', thumbnailUrl);
            }
            
            // Reapply all settings
            if (autoPlay) newMuxPlayerEl.setAttribute('autoplay', '');
            if (muted) newMuxPlayerEl.setAttribute('muted', '');
            if (loop) newMuxPlayerEl.setAttribute('loop', '');
            if (startTime !== undefined) newMuxPlayerEl.setAttribute('start-time', startTime.toString());
            if (preload) newMuxPlayerEl.setAttribute('preload', preload);
            newMuxPlayerEl.setAttribute('stream-type', streamType);
            if (controls) {
              newMuxPlayerEl.setAttribute('controls', '');
              if (defaultShowCaptions) newMuxPlayerEl.setAttribute('default-show-captions', '');
            }
            if (primaryColor) newMuxPlayerEl.setAttribute('primary-color', primaryColor);
            if (secondaryColor) newMuxPlayerEl.setAttribute('secondary-color', secondaryColor);
            if (maxResolution) newMuxPlayerEl.setAttribute('max-resolution', maxResolution);
            if (tokens?.playback) newMuxPlayerEl.setAttribute('playback-token', tokens.playback);
            
            // Apply styles
            newMuxPlayerEl.style.height = '100%';
            newMuxPlayerEl.style.width = '100%';
            newMuxPlayerEl.style.borderRadius = '0.5rem';
            newMuxPlayerEl.style.display = 'block';
            
            // Add to DOM
            playerRef.current?.appendChild(newMuxPlayerEl);
            
            // Try to restore position after the player loads
            newMuxPlayerEl.addEventListener('loadedmetadata', () => {
              if (currentTime > 0) {
                newMuxPlayerEl.currentTime = currentTime;
              }
            }, { once: true });
          }, 500);
        } else if (errorRetryCount >= maxErrorRetries) {
          console.error('[MuxPlayer] Max error retries reached, unable to recover');
        }
      });
      
      // Prevent buffer clearing when paused
      muxPlayerEl.addEventListener('pause', () => {
        console.log('[MuxPlayer] Video paused, starting keep-alive');
        // Keep the connection alive by periodically checking readyState
        keepAliveInterval = setInterval(() => {
          const video = muxPlayerEl.querySelector('video');
          if (video && video.readyState < 2) {
            console.log('[MuxPlayer] Buffer might be stale, preloading...');
            // Trigger a small preload to keep buffer fresh
            if (video.load) {
              const currentTime = video.currentTime;
              video.load();
              video.currentTime = currentTime;
            }
          }
        }, 30000); // Check every 30 seconds
      });
      
      muxPlayerEl.addEventListener('play', () => {
        console.log('[MuxPlayer] Video playing, stopping keep-alive');
        if (keepAliveInterval) {
          clearInterval(keepAliveInterval);
          keepAliveInterval = null;
        }
      });
    }
    
    // Cleanup function
    return () => {
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
      }
    };
  }, [
    scriptLoaded, 
    validPlaybackId, 
    title, 
    poster, 
    autoPlay, 
    startTime, 
    muted, 
    loop, 
    thumbnailTime, 
    preload, 
    controls, 
    primaryColor, 
    secondaryColor, 
    maxResolution, 
    captionUrl,
    defaultShowCaptions,
    streamType,
    tokens,
    videoReady
  ]);

  // Merge the forwarded ref with our internal ref
  useEffect(() => {
    if (ref && playerRef.current) {
      if (typeof ref === 'function') {
        ref(playerRef.current);
      } else {
        ref.current = playerRef.current;
      }
    }
  }, [ref, scriptLoaded]);

  // If no valid playbackId, show error state
  if (!validPlaybackId) {
    return (
      <div className={`relative w-full aspect-video ${className}`}>
        <div className="w-full aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
          <div className="text-center p-6">
            <p className="text-sm text-muted-foreground">No video available</p>
            <p className="text-xs text-muted-foreground mt-2">
              Please upload a video to continue
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative w-full aspect-video ${className}`}>
      {checkingAvailability && !videoReady && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center z-10">
          <div className="text-center p-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Processing video...</p>
            <p className="text-xs text-muted-foreground mt-2">
              This may take a few moments after upload
            </p>
          </div>
        </div>
      )}
      <div 
        ref={playerRef} 
        className={`w-full aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden`} 
        data-testid="mux-player-container"
      />
    </div>
  );
});

MuxPlayer.displayName = 'MuxPlayer';

export default MuxPlayer;